import join from 'join-array';
import {
  ContentWorkflow,
  TechDomainDisplayNames,
} from '@amzn/awscat-pattern-library-service-model';
import {
  ColumnLayout,
  ExpandableSection,
  Grid,
  Icon,
  Link,
  StatusIndicator,
} from '@amzn/awsui-components-core';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { contentAudienceBadgeDisplayNames } from '../utils/DisplayNames';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { IFeedbackServiceClient, FeedbackServiceFactory } from '../../api/FeedbackService';
import { FeedbackRatingSystem, FeedbackRating } from '../feedback-service';
import {
  awsServiceDisplayNames,
  rTypeDisplayNames,
  workloadDisplayNames,
} from '../utils/DisplayNames';
import { extractUserAlias } from '../../api/UtilsApi';
import { AppContext } from '../../contexts/AppContext';
import { convertEntities } from '@amzn/awscat-editor';

interface Props {
  workflow: ContentWorkflow;
  showMetadataExpanded?: boolean;
}

// @ts-expect-error TS7006 - strict migration
const joinValues = (valueList) => {
  if (!valueList || valueList.length === 0) {
    return null;
  }
  const joinedValues = join({
    array: valueList,
    separator: ', ',
    last: ' and ',
    max: 10,
    // @ts-expect-error TS7006 - strict migration
    maxMessage: (missed) => `(${missed} more...)`,
    // @ts-expect-error TS7006 - strict migration
    each: (pr) => pr,
  });
  return joinedValues;
};

const feedbackServiceClient: IFeedbackServiceClient = FeedbackServiceFactory.create();
const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();

const ContentInfoDetails = ({ showMetadataExpanded, workflow }: Props) => {
  const {
    contentAudience,
    contentId,
    draftContentMetadata,
    draftContentTypeMetadata,
    draftTemplateMetadata,
    ownerId,
    ownerProfile,
    barRaiserId,
  } = workflow;
  const { codeLinks } = draftContentTypeMetadata;
  const { contentName, description } = draftContentMetadata;
  const { awsServices, mainTechDomain, rType, techDomains, workload } = draftTemplateMetadata;

  const [authors, setAuthors] = useState('');
  const [reviewers, setReviewers] = useState('');
  const [likesCount, setLikesCount] = useState(null);
  const [loading, setIsLoading] = useState(true);

  const appContext = useContext(AppContext);

  const hasCode = !!(codeLinks?.length > 0);
  const codeLink = hasCode ? codeLinks[0] : null;
  const mainTechnology = TechDomainDisplayNames.get(mainTechDomain);
  const additionalTechnologyList = techDomains
    ? // @ts-expect-error TS7006 - strict migration
      techDomains.map((techDomain) => TechDomainDisplayNames.get(techDomain))
    : null;
  const additionalTechnologies = joinValues(additionalTechnologyList);
  const awsServiceList = awsServices
    ? // @ts-expect-error TS7006 - strict migration
      awsServices.map((awsService) => awsServiceDisplayNames.get(awsService))
    : null;
  const awsServiceNames = joinValues(awsServiceList);
  // @ts-expect-error TS7006 - strict migration
  const workloadList = workload ? workload.map((wkLoad) => workloadDisplayNames.get(wkLoad)) : null;
  const workloadNames = joinValues(workloadList);
  const rTypeName = rTypeDisplayNames.get(rType);
  const barRaiserAlias = barRaiserId?.replace('AmazonCorporate_', '');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [{ accessUsers: coAuthors }, { accessUsers: contentReviewers }] = await Promise.all([
          mplServiceClient.listContentSharedWith(contentId),
          mplServiceClient.listContentReviewers(contentId),
        ]);
        const authorNameList = [extractUserAlias(ownerProfile ? ownerProfile.userId : ownerId)];
        authorNameList.push(...coAuthors.map((coAuthor) => extractUserAlias(coAuthor.userId)));
        const authorNames = joinValues(authorNameList);
        setAuthors(authorNames);
        const reviewerNameList = contentReviewers.map((contentReviewer) =>
          extractUserAlias(contentReviewer.userId),
        );
        const reviewerNames = joinValues(reviewerNameList);
        setReviewers(reviewerNames);
        const feedbackStatResponse = await feedbackServiceClient.listFeedback(
          [contentId],
          FeedbackRatingSystem.LikeDislike,
        );
        const ratingCount =
          // @ts-expect-error TS2531 - strict migration
          feedbackStatResponse?.[0].counts?.find((rc) => rc.value === FeedbackRating.Like)?.count ??
          'None';
        // @ts-expect-error TS2345 - strict migration
        setLikesCount(ratingCount);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [contentId, ownerId, ownerProfile]);

  const contentInfo = (
    <ColumnLayout columns={1}>
      <ExpandableSection header="Details" variant="footer" defaultExpanded>
        <div className="padding-5">
          <div className="form-field-label">Content Id</div>
          {contentId}
        </div>
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]} disableGutters>
          <div>
            <div className="padding-5">
              <div className="form-field-label">Title</div>
              {convertEntities(contentName, appContext.entities)}
            </div>
            <div className="padding-5">
              <div className="form-field-label">Author / Co-author</div>
              {authors}
            </div>
            <div className="padding-5">
              <div className="form-field-label">Intended audience</div>
              {contentAudienceBadgeDisplayNames.get(contentAudience)}
            </div>
          </div>
          <div>
            <div className="padding-5">
              <p title="Likes">
                <span className="valign-bottom">
                  <Icon url="/images/thumb-up-voted.svg" variant="normal" alt="Likes" />
                  <span className="padding-left-5">{likesCount ?? 'None'}</span>
                </span>
              </p>
            </div>
            <div className="padding-5">
              <div className="form-field-label">Has code?</div>
              {hasCode ? 'Yes' : 'No'}
            </div>
            {codeLink?.appsecTicket && (
              <div className="padding-5">
                <div className="form-field-label">
                  AppSec Security Guidance for AWS Content ticket
                </div>
                <Link fontSize="body-s" external href={codeLink.appsecTicket}>
                  {codeLink.appsecStatus}
                </Link>
              </div>
            )}
            {codeLink?.openSourceTicket && (
              <div className="padding-5">
                <div className="form-field-label">Open source ticket</div>
                <Link fontSize="body-s" external href={codeLink.openSourceTicket}>
                  {codeLink.openSourceStatus}
                </Link>
              </div>
            )}
          </div>
        </Grid>
      </ExpandableSection>
      <ExpandableSection header="Metadata" variant="footer" defaultExpanded={showMetadataExpanded}>
        <div>
          <div className="padding-5">
            <div className="form-field-label">Overview</div>
            {convertEntities(description, appContext.entities) ?? 'None'}
          </div>
        </div>
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]} disableGutters>
          <div>
            <div className="padding-5">
              <div className="form-field-label">Peer reviewed by</div>
              {reviewers ?? 'None'}
            </div>
            <div className="padding-5">
              <div className="form-field-label">Main technology</div>
              {mainTechnology ?? 'None'}
            </div>
            <div className="padding-5">
              <div className="form-field-label">Additional technologies</div>
              {additionalTechnologies ?? 'None'}
            </div>
            <div className="padding-5">
              <div className="form-field-label">AWS Services</div>
              {awsServiceNames ?? 'None'}
            </div>
          </div>
          <div>
            <div className="padding-5">
              <div className="form-field-label">Workload</div>
              {workloadNames ?? 'None'}
            </div>
            <div className="padding-5">
              <div className="form-field-label">RType</div>
              {rTypeName ?? 'None'}
            </div>
            <div className="padding-5">
              <div className="form-field-label">Bar Raiser Alias</div>
              {barRaiserAlias ?? 'None'}
            </div>
          </div>
        </Grid>
      </ExpandableSection>
    </ColumnLayout>
  );

  if (loading) {
    return <StatusIndicator type="loading">Loading...</StatusIndicator>;
  }

  return <div>{contentInfo}</div>;
};
export default ContentInfoDetails;
